import { CLOSETPaymentCurrency, CLOSETPaymentMethod } from '../../../groups';
import { BillDetailType, BillType } from 'types/plansales/billing';

// /closet/groups api 전용
// [EN] Picks which column the keyword is matched against. Values are the server
//      contract (CLOSET-17914). The server also accepts `0` and an omitted
//      parameter as "match all four columns", but that full scan is what the
//      ticket removes, so this screen never produces it.
// [KO] 키워드를 어느 컬럼에 대조할지 고른다. 값은 서버 계약(CLOSET-17914)이다.
//      서버는 `0` 과 파라미터 생략도 "4개 컬럼 전부 대조" 로 받지만, 그 풀스캔이
//      바로 이 티켓이 없애려는 것이라 이 화면은 그 값을 만들지 않는다.
export enum CLOSETGroupsSearchType {
  GROUP_ID = 1,
  GROUP_NAME = 2,
  EMAIL = 3,
  DOMAIN = 4,
}

export enum CLOSETGroupsPlanType {
  FREE = 1,
  STANDARD = 2,
  STANDARD_TRIAL = 3,
  PREMIUM = 4,
  PREMIUM_TRIAL = 5,
  ACADEMIC = 6,
  ADVANCED = 7,
}

export interface GetCLOSETGroupsParams {
  startDate: string;
  endDate: string;
  planTypes: CLOSETGroupsPlanType[];
  paymentMethods: CLOSETPaymentMethod[];
  keyword?: string;
  managerEmail?: string;
  // [EN] Required: every search names exactly one column. Leaving it optional
  //      would let a caller silently fall back to the unified full scan.
  // [KO] 필수다. 모든 검색은 컬럼 하나를 지목한다. optional 로 두면 호출부가 조용히
  //      통합 풀스캔으로 되돌아갈 수 있다.
  searchType: CLOSETGroupsSearchType;
  pageSize: number;
  pageNo: number;
}

export interface CLOSETGroupItem {
  groupId: string;
  groupAdminEmail: string;
  planType: CLOSETGroupsPlanType;
  paymentMethod: CLOSETPaymentMethod;
  currentCreditBalance: number | null;
  subscriptionTerm: { startDate: string; endDate: string };
  measurementPeriodTerm: { startDate: string; endDate: string };
  registeredDate: string;
  isNearSubscriptionExpiry: boolean;
}

export interface GetCLOSETGroupsListResponse {
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: CLOSETGroupItem[];
}

export interface CLOSETGroupsManager {
  id: number;
  userId: number;
  email: string;
}

export type GetCLOSETGroupsManagersResponse = CLOSETGroupsManager[];

export interface APICLOSETGroupsDefaultParams {
  groupId: string;
}

export interface GetCLOSETGroupsMembersSummaryResponse {
  groupAdminCount: number;
  adminCount: number;
  collaboratorCount: number;
  editorCount: number;
  viewerCount: number;
  groupOperatorCount: number;
}

export interface GetCLOSETGroupInfoResponse {
  groupId: string;
  groupName: string;
  groupAdminEmail: string;
  planType: CLOSETGroupsPlanType;
  paymentMethod: CLOSETPaymentMethod;
  paymentCurrency: CLOSETPaymentCurrency;
  managers: GetCLOSETGroupsManagersResponse;
}

export interface PostCLOSETGroupsManagersParams extends APICLOSETGroupsDefaultParams {
  userId: number;
}

export interface DeleteCLOSETGroupsManagersParams extends APICLOSETGroupsDefaultParams {
  userId: number;
}

export type PostCLOSETGroupsManagersResponse = CLOSETGroupsManager;

export interface GetCLOSETGroupsUsageItem {
  maximumUsage: number | null;
  currentUsage: number;
}

export interface GetCLOSETGroupsUsageResponse {
  plan: CLOSETGroupsPlanType;
  render: GetCLOSETGroupsUsageItem;
  workroom: GetCLOSETGroupsUsageItem;
  file: GetCLOSETGroupsUsageItem;
  user: GetCLOSETGroupsUsageItem & { supportUsage: number };
  company: GetCLOSETGroupsUsageItem;
  embed: GetCLOSETGroupsUsageItem;
  apiCall: GetCLOSETGroupsUsageItem;
}

export interface GetCLOSETGroupsSubscriptionHistoryParams extends APICLOSETGroupsDefaultParams {
  pageSize?: number;
  pageNo?: number;
}

export interface GetCLOSETGroupsSubscriptionHistoryItem {
  plan: string;
  paymentMethod: string;
  registeredDate: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  measurementStartDate: string;
  measurementEndDate: string;
  registeredByEmail: string;
  status: string;
}

export interface GetCLOSETGroupsSubscriptionHistoryResponse {
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: GetCLOSETGroupsSubscriptionHistoryItem[];
}

export interface GetCLOSETGroupsBillingHistoryParams extends APICLOSETGroupsDefaultParams {
  pageSize?: number;
  pageNo?: number;
}

export interface GetCLOSETGroupsBillingHistoryItemDetails {
  amount: number;
  quantity: number;
  type: BillDetailType;
  startDate: string;
  endDate: string;
}

export interface GetCLOSETGroupsBillingHistoryItem {
  type: BillType;
  plan: CLOSETGroupsPlanType;
  previousPlan: CLOSETGroupsPlanType;
  date: string;
  registeredDate: string;
  amount: number;
  balance: number;
  invoiceId: string;
  registeredById: number;
  registeredByEmail: string;
  details: GetCLOSETGroupsBillingHistoryItemDetails[];
}

export interface GetCLOSETGroupsBillingHistoryResponse {
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: GetCLOSETGroupsBillingHistoryItem[];
}

export interface GetCLOSETGroupsCommentsParams {
  groupId: string;
  pageSize?: number;
  pageNo?: number;
}

export interface PostCLOSETGroupsCommentsParams {
  groupId: string;
  description: string;
}

export interface DeleteCLOSETGroupsCommentsParams {
  groupId: string;
  commentId: number;
}

export interface PutCLOSETGroupsCommentsParams {
  groupId: string;
  commentId: number;
  description: string;
}

export interface GetCLOSETGroupsCommentItem {
  id: number;
  no: number;
  description: string;
  postedDateUtc: string;
  updatedDate: string;
  operator: string;
  postedDateKstTime: string;
}

export interface GetCLOSETGroupsCommentsResponse {
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  items: GetCLOSETGroupsCommentItem[];
}

export interface PostCLOSETGroupsUsageParams {
  groupId: string;
  render?: number;
  embed?: number;
  apiCall?: number;
}

export interface PostCLOSETGroupsUsageResponse {
  groupId: string;
  measurementPeriodId: number;
  before: {
    apiCall: number;
    embed: number;
    render: number;
  };
  after: {
    apiCall: number;
    embed: number;
    render: number;
  };
}

export interface GetCLOSETGroupsClofabFeatureResponse {
  groupId: string;
  unlimitedZfabUpload: boolean;
}

// [EN] One flag per request, and every flag is optional — `PATCH` leaves out what it
//      does not send. Widening this to the full DTO would let an old front end zero
//      out a flag added later (lost update).
// [KO] 요청 하나에 플래그 하나만 담고, 모든 플래그는 선택이다 — `PATCH` 는 보내지 않은
//      값을 건드리지 않는다. 이 타입을 전체 DTO 로 넓히면 나중에 추가된 플래그를 오래된
//      프론트가 0 으로 덮어쓴다(lost update).
export interface PatchCLOSETGroupsClofabFeatureParams extends APICLOSETGroupsDefaultParams {
  unlimitedZfabUpload?: boolean;
}
